import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { filter, sortBy } from 'underscore';
const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
    const { socket } = useAuth();
    const [conversationsMessages, setConversationsMessages] = useState({});
    const [conversations, setConversationsState] = useState([]);
    const [humanInControl, setHumanInControlState] = useState(false);

    useEffect(() => {
        if (!socket) return;

        const handleNewMessage = (message) => {
            setMessages(message.conversationId, [message]);
        };

        socket.on('nuevo_mensaje_botpress', handleNewMessage);

        return () => {
            socket.off('nuevo_mensaje_botpress', handleNewMessage);
        };
    }, [socket]);

    const getMessages = useCallback((conversationId) => {
        return conversationsMessages[conversationId] || [];
    }, [conversationsMessages]);

    const setMessages = useCallback((conversationId, messages, options = {}) => {
        setConversationsMessages(prev => {
            const existingMessages = prev[conversationId] || [];
            const seenIds = new Set(existingMessages.map(msg => msg.id));
            const newUniqueMessages = messages.filter(msg => !seenIds.has(msg.id));
            return {
                ...prev,
                [conversationId]: options.replace
                    ? messages
                    : [...existingMessages, ...newUniqueMessages]
            };
        });

        if (messages.length > 0) {
            const messagesForLastMessage = filter(messages, msg => msg.show !== false);
            const lastMessage = messagesForLastMessage[messagesForLastMessage.length - 1];

            setConversationsState(prev => {
                const updatedConversations = prev.map(c =>
                    c.id === conversationId
                        ? {
                            ...c,
                            lastMessage,
                            updatedAt: new Date().toISOString() // Actualiza el campo updatedAt
                        }
                        : c
                );

                const conversationExists = prev.some(c => c.id === conversationId);
                if (!conversationExists) {
                    let updateConversation = {
                        id: conversationId,
                        lastMessage: lastMessage,
                        updatedAt: new Date().toISOString() // Establece updatedAt para nuevas conversaciones
                    };
                    updatedConversations.push(updateConversation);
                }

                return sortBy(updatedConversations, 'updatedAt').reverse();
            });
        }
    }, []);

    const getConversations = useCallback(() => {
        return sortBy(conversations, 'updatedAt').reverse();
    }, [conversations]);

    const setConversations = useCallback((newConversations) => {
        setConversationsState(newConversations);
    }, []);

    const getHumanInControl = useCallback(() => humanInControl, [humanInControl]); // Getter para humanInControl
    const setHumanInControl = useCallback((value) => setHumanInControlState(value), []); // Setter para humanInControl

    return (
        <ChatContext.Provider value={{
            getMessages,
            setMessages,
            getConversations,
            setConversations,
            getHumanInControl,
            setHumanInControl
        }}>
            {children}
        </ChatContext.Provider>
    );
};

export const useChat = () => useContext(ChatContext);